import { useState, type FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiError, type Liability, type LiabilityKind } from '@greencandle/api-client'
import { api } from '../api'
import { CURRENCIES } from '../currencies'


/**
 * Why a figure cannot be read as a number, in the reader's words, or null when it can.
 *
 * <p>Checked here rather than left to the server because the server sees "18 000 0" as an
 * unparseable token and can only say so after a round trip; the form knows what was typed and
 * can name the likely intent. Spaces are called out on their own - writing a thousands separator
 * is the mistake people actually make, and it is not a typo to be told off for.
 */
function numberProblem(label: string, value: string): string | null {
  const raw = value.trim()
  // Emptiness is not a complaint. The input carries `required`, so the browser blocks an empty
  // submit on its own; shouting "required" in red at a form nobody has typed in yet is noise,
  // and worse, it hides the message about the field they are actually working on.
  if (!raw) {
    return null
  }
  // A literal non-breaking space is what a paste from a spreadsheet carries, and
  // JS \s already matches it.
  if (/\s/.test(raw)) {
    const withoutSpaces = raw.replace(/\s/g, '')
    const suggestion = Number.isFinite(Number(withoutSpaces))
      ? ` Did you mean ${withoutSpaces}?`
      : ''
    return `${label} cannot contain spaces — write it as one number.${suggestion}`
  }
  if (raw.includes(',')) {
    return `${label} must use a full stop for decimals, not a comma.`
  }
  if (!Number.isFinite(Number(raw))) {
    return `${label} must be a number, and "${raw}" is not one.`
  }
  return null
}

const KINDS: ReadonlyArray<readonly [LiabilityKind, string]> = [
  ['MORTGAGE', 'Mortgage'],
  ['LOAN', 'Loan'],
  ['INSTALMENT', 'Instalment plan'],
  ['CREDIT_LINE', 'Credit line'],
  ['MARGIN', 'Margin loan'],
  ['OTHER', 'Other'],
]

/**
 * AST-2. Records what is owed, so net worth is assets minus debt rather than assets alone.
 *
 * <p>The backend has had full CRUD for liabilities since AST-2 and the client could only read
 * them, so the only debt this book could show was none. Everything the audit wizard says about
 * capacity - how many months of obligations the emergency fund covers, whether a shock breaches
 * a solvency threshold, whether income covers the payments - divides by a number that did not
 * exist.
 *
 * <p>Three fields are required and the rest are optional on purpose: a balance and a name make a
 * debt real, while the rate, the maturity and the monthly payment are what make it possible to
 * say anything useful about it later. They are offered, not demanded.
 */
export function LiabilityForm({
  accountId,
  existing,
  onDone,
}: {
  accountId: string
  existing?: Liability
  onDone: () => void
}) {
  const queryClient = useQueryClient()
  const editing = existing !== undefined

  const [kind, setKind] = useState<LiabilityKind>(existing?.kind ?? 'MORTGAGE')
  const [name, setName] = useState(existing?.name ?? '')
  const [principalOutstanding, setPrincipal] = useState(existing?.principalOutstanding ?? '')
  const [outstandingAsOf, setAsOf] = useState(
    existing?.outstandingAsOf ?? new Date().toISOString().slice(0, 10),
  )
  const [currency, setCurrency] = useState(existing?.currency ?? 'USD')
  const [monthlyPayment, setMonthlyPayment] = useState(existing?.monthlyPayment ?? '')
  const [annualRatePercent, setRate] = useState(existing?.annualRatePercent ?? '')
  const [maturesOn, setMaturesOn] = useState(existing?.maturesOn ?? '')
  const [notes, setNotes] = useState(existing?.notes ?? '')

  // The margin balance the broker already reports. A margin loan entered here as well would be
  // subtracted twice, because net worth is assets + cash - debt and the debit is already in the
  // cash side. Warned rather than blocked: someone may genuinely be tracking borrowing this
  // system has no statement for.
  const netWorth = useQuery({
    queryKey: ['net-worth', accountId],
    queryFn: () => api.netWorth(accountId),
  })
  const negativeCash = netWorth.data?.cash != null && Number(netWorth.data.cash) < 0
  const doubleCounting = kind === 'MARGIN' && negativeCash

  // Every numeric field, checked before anything is sent. The first problem is the one shown:
  // a list of four complaints about a half-filled form is noise, and fixing the first usually
  // fixes the rest.
  const problem =
    numberProblem('Outstanding', principalOutstanding)
    ?? numberProblem('Monthly payment', monthlyPayment)
    ?? numberProblem('Annual rate %', annualRatePercent)

  const save = useMutation({
    mutationFn: () => {
      const request = {
        kind,
        name,
        principalOutstanding,
        outstandingAsOf,
        currency,
        monthlyPayment: monthlyPayment || null,
        annualRatePercent: annualRatePercent || null,
        maturesOn: maturesOn || null,
        notes: notes || null,
      }
      return editing
        ? api.updateLiability(accountId, existing.id, request)
        : api.createLiability(accountId, request)
    },
    onSuccess: () => {
      // Net worth, the debt ratio and every coverage line move with this.
      void queryClient.invalidateQueries()
      onDone()
    },
  })

  function submit(event: FormEvent) {
    event.preventDefault()
    if (problem) {
      return
    }
    save.mutate()
  }

  return (
    <form className="card" onSubmit={submit} style={{ marginBottom: 16 }}>
      <h3 className="card-title">{editing ? 'Edit liability' : 'New liability'}</h3>
      <p className="muted" style={{ marginTop: 0 }}>
        A mortgage, a loan, a credit line. The balance is a mark you carry forward with the date
        it was true — nothing here amortises it, so update it when a statement arrives.
      </p>

      <div className="entry-grid">
        <label>
          Kind
          <select value={kind} onChange={(e) => setKind(e.target.value as LiabilityKind)}>
            {KINDS.map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
        </label>
        <label>
          Name
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Flat mortgage"
            required
          />
        </label>
        <label>
          Outstanding
          <input
            value={principalOutstanding}
            onChange={(e) => setPrincipal(e.target.value)}
            inputMode="decimal"
            placeholder="180000"
            required
          />
        </label>
        <label>
          As of
          <input type="date" value={outstandingAsOf} onChange={(e) => setAsOf(e.target.value)} />
        </label>
        <label>
          Currency
          <select value={currency} onChange={(e) => setCurrency(e.target.value)}>
            {CURRENCIES.map((code) => (
              <option key={code} value={code}>{code}</option>
            ))}
          </select>
        </label>
        <label>
          Monthly payment
          <input
            value={monthlyPayment}
            onChange={(e) => setMonthlyPayment(e.target.value)}
            inputMode="decimal"
            placeholder="1140"
          />
        </label>
        <label>
          Annual rate %
          <input
            value={annualRatePercent}
            onChange={(e) => setRate(e.target.value)}
            inputMode="decimal"
            placeholder="3.5"
          />
        </label>
        <label>
          Matures on
          <input type="date" value={maturesOn} onChange={(e) => setMaturesOn(e.target.value)} />
        </label>
        <label style={{ gridColumn: '1 / -1' }}>
          Notes
          <input value={notes} onChange={(e) => setNotes(e.target.value)} />
        </label>
      </div>

      <p className="muted" style={{ fontSize: 12 }}>
        The monthly payment is what the audit measures an emergency fund against, and the rate and
        maturity are how it tells an expensive short debt from a cheap long one. Optional, and
        worth filling in.
      </p>

      {doubleCounting && (
        <div className="notice warn" role="status" style={{ marginTop: 8 }}>
          Your broker already reports a negative cash balance, and net worth subtracts it. Adding
          the same borrowing as a margin liability would count it twice. Only record this if it is
          money owed somewhere the statements do not cover.
        </div>
      )}

      {problem && (
        <div className="notice error" role="alert" style={{ marginTop: 8 }}>
          {problem}
        </div>
      )}

      {save.isError && (
        <div className="notice error" role="alert" style={{ marginTop: 8 }}>
          {save.error instanceof ApiError
            ? (save.error.problem.detail ?? save.error.message)
            : 'Could not reach the API.'}
        </div>
      )}

      <div style={{ marginTop: 12 }}>
        <button type="submit" className="primary" disabled={save.isPending || problem !== null}>
          {save.isPending ? 'Saving…' : editing ? 'Save' : 'Create'}
        </button>{' '}
        <button type="button" onClick={onDone}>Cancel</button>
      </div>
    </form>
  )
}
